/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import {
  Box,
  Flex,
  Link,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
} from '@tonic-one/react';
import isEqual from 'lodash/isEqual';
import React from 'react';
import { useTranslation } from 'react-i18next';
import ButtonTab from 'src/components/Common/ButtonTab';
import { TitleLabel } from 'src/components/Common/FormComponent';
import NotifyAdmin from 'src/components/FilterNotification/NotifyAdmin';
import NotifyUser from 'src/components/FilterNotification/NotifyUser';
import {
  EnableDisableStatus,
  ExchangeOnlinePolicyItem,
} from 'src/pages/Policy/utils/types';

import {
  storeSelector,
  templateSelector,
  usePolicyContext,
} from '@Policy/store';
import { useWhyDidYouUpdate } from 'src/utils/hooks';
import { PolicyModeEnum, PolicyTypeEnum } from '@Policy/utils/constants';
import {
  ddaActionDataSelector,
  ddaAdvancedOptionConfigSelector,
} from '@Policy/store/filters/virtualAnalyzer';
import Actions from '../../Components/Actions';
import AdvancedOptionModal from '../../Components/AdvancedOptionModal';

export const VaAction = () => {
  // get store from context
  const contextStore = usePolicyContext();
  // get store selector
  const contextSelector = storeSelector(contextStore);
  // get state from store
  const actionData = contextSelector(ddaActionDataSelector, isEqual);
  useWhyDidYouUpdate('VaAction', { actionData });
  const policySelector = templateSelector(
    contextStore
  )<ExchangeOnlinePolicyItem>;
  const policyMode = policySelector((s) => s.policyMode);
  return (
    <Actions
      actionData={actionData}
      disabledAll={policyMode === PolicyModeEnum.DefaultMonitorPolicy}
    />
  );
};

export const VaAdvancedOption = ({
  replaceFileNameShow,
  availableTokenShow,
}: {
  availableTokenShow: boolean;
  replaceFileNameShow: boolean;
}) => {
  const { t } = useTranslation();
  // get store from context
  const contextStore = usePolicyContext();
  // get store selector
  const contextSelector = storeSelector(contextStore);
  // get state and action from store
  const advancedOptionConfig = contextSelector(
    ddaAdvancedOptionConfigSelector,
    isEqual
  );
  // get action from store
  const setDdaState = contextSelector((s) => s.setDdaState);
  // advanced option
  const [isVisible, setIsVisible] = React.useState(false);
  const handleAdvancedOptionClose = React.useCallback(() => {
    setIsVisible(false);
  }, []);
  const handleAdvancedOptionSave = React.useCallback(
    ({ replaceText }: { replaceText: string }) => {
      setDdaState({
        ddaReplacementText: replaceText,
      });
    },
    []
  );
  const policyType = templateSelector(contextStore)((s) => s.policyType);
  useWhyDidYouUpdate('VA VaAdvancedOption', {
    advancedOptionConfig,
    isVisible,
  });
  return (
    <>
      <Link mr="24px" onClick={() => setIsVisible(!isVisible)}>
        {t('policy.action.advanced.title')}
      </Link>
      <AdvancedOptionModal
        replacementTextDesc={
          [
            PolicyTypeEnum.SharePoint,
            PolicyTypeEnum.OneDrive,
            PolicyTypeEnum.Box,
            PolicyTypeEnum.Dropbox,
            PolicyTypeEnum.GoogleDrive,
            PolicyTypeEnum.Teams,
          ].includes(policyType)
            ? t('policy.action.replaceAction.quarantineOrDelete.desc')
            : t('policy.action.replaceAction.desc')
        }
        availableTokenShow={availableTokenShow}
        replaceFileNameShow={replaceFileNameShow}
        isVisible={isVisible}
        onClose={handleAdvancedOptionClose}
        config={advancedOptionConfig}
        saveConfig={handleAdvancedOptionSave}
      />
    </>
  );
};

export const VaNotification = () => {
  const { t } = useTranslation();
  // get store from context
  const contextStore = usePolicyContext();
  // get store selector
  const contextSelector = storeSelector(contextStore);
  const policySelector = templateSelector(
    contextStore
  )<ExchangeOnlinePolicyItem>;
  // get state from store
  const ntfEnableNotifyRecipient = policySelector(
    (s) => s.tmDdafilters.notification.ntfEnableNotifyRecipient
  );
  const ntfInternalRecipientSubject = policySelector(
    (s) => s.tmDdafilters.notification.ntfInternalRecipientSubject
  );
  const ntfInternalRecipientMessage = policySelector(
    (s) => s.tmDdafilters.notification.ntfInternalRecipientMessage
  );
  const ntfEnableNotifyAdmin = policySelector(
    (s) => s.tmDdafilters.notification.ntfEnableNotifyAdmin
  );
  const ntfToAccount = policySelector(
    (s) => s.tmDdafilters.notification.ntfToAccount
  );
  const ntfAdminSubject = policySelector(
    (s) => s.tmDdafilters.notification.ntfAdminSubject
  );
  const ntfAdminMessage = policySelector(
    (s) => s.tmDdafilters.notification.ntfAdminMessage
  );
  const ntfAdminNotifySettingType = policySelector(
    (s) => s.tmDdafilters.notification.ntfAdminNotifySettingType
  );
  const ntfAdminIntervalNum = policySelector(
    (s) => s.tmDdafilters.notification.ntfAdminIntervalNum
  );
  const ntfAdminIntervalType = policySelector(
    (s) => s.tmDdafilters.notification.ntfAdminIntervalType
  );
  const ntfAdminOccurenceNum = policySelector(
    (s) => s.tmDdafilters.notification.ntfAdminOccurenceNum
  );
  const ntfRecipientGroupId = policySelector(
    (s) => s.tmDdafilters.notification.ntfRecipientGroupId
  );
  const ntfRecipientGroupEnabled = policySelector(
    (s) => s.tmDdafilters.notification.ntfRecipientGroupEnabled
  );
  const recipientGroups = contextSelector((s) => s.recipientGroups);
  // get action from store
  const setDdaNtfState = contextSelector((s) => s.setDdaNtfState);
  // local state
  const handleNtfEnableNotifyRecipientChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setDdaNtfState({
        ntfEnableNotifyRecipient: e.target.checked ? 1 : 0,
      });
    },
    []
  );
  const handleNtfInternalRecipientSubjectChange = React.useCallback(
    (sbuject: string) =>
      setDdaNtfState({ ntfInternalRecipientSubject: sbuject }),
    []
  );
  const handleNtfInternalRecipientMessageChange = React.useCallback(
    (message: string) =>
      setDdaNtfState({ ntfInternalRecipientMessage: message }),
    []
  );
  const handleNtfEnableNotifyAdminChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setDdaNtfState({ ntfEnableNotifyAdmin: e.target.checked ? 1 : 0 });
    },
    []
  );
  const handleNtfToAccountChange = React.useCallback(
    (toAccount: string) => setDdaNtfState({ ntfToAccount: toAccount }),
    []
  );
  const handleNtfAdminSubjectChange = React.useCallback(
    (subject: string) => setDdaNtfState({ ntfAdminSubject: subject }),
    []
  );
  const handleNtfAdminMessageChange = React.useCallback(
    (message: string) => setDdaNtfState({ ntfAdminMessage: message }),
    []
  );
  const handleNtfAdminNotifySettingTypeChange = React.useCallback(
    (notifySettingType: number) =>
      setDdaNtfState({ ntfAdminNotifySettingType: notifySettingType }),
    []
  );
  const handleNtfAdminIntervalNumChange = React.useCallback(
    (intervalNum: number) =>
      setDdaNtfState({ ntfAdminIntervalNum: intervalNum }),
    []
  );
  const handleNtfAdminIntervalTypeChange = React.useCallback(
    (intervalType: number) =>
      setDdaNtfState({ ntfAdminIntervalType: intervalType }),
    []
  );
  const handleNtfAdminOccurenceNumChange = React.useCallback(
    (occurenceNum: number) =>
      setDdaNtfState({ ntfAdminOccurenceNum: occurenceNum }),
    []
  );
  const handleNtfRecipientGroupIdChange = React.useCallback(
    (recipientGroupId: number) => {
      setDdaNtfState({ ntfRecipientGroupId: recipientGroupId });
    },
    []
  );
  const handleNtfRecipientGroupEnabledChange = React.useCallback(
    (enabled: EnableDisableStatus) => {
      setDdaNtfState({ ntfRecipientGroupEnabled: enabled });
    },
    []
  );
  useWhyDidYouUpdate('VaNotification', {
    ntfEnableNotifyRecipient,
    ntfInternalRecipientSubject,
    ntfInternalRecipientMessage,
    ntfEnableNotifyAdmin,
    ntfToAccount,
    ntfAdminSubject,
    ntfAdminMessage,
    ntfAdminNotifySettingType,
    ntfAdminIntervalNum,
    ntfAdminIntervalType,
    ntfAdminOccurenceNum,
    ntfRecipientGroupId,
    ntfRecipientGroupEnabled,
    recipientGroups,
  });
  return (
    <Tabs mt="24px">
      <TabList>
        <ButtonTab>{t('policy.notification.admin')}</ButtonTab>
        <ButtonTab>{t('policy.notification.user')}</ButtonTab>
      </TabList>
      <TabPanels py="24px">
        <TabPanel>
          <NotifyAdmin
            status={ntfEnableNotifyAdmin}
            toAccount={ntfToAccount}
            notifySettingType={ntfAdminNotifySettingType}
            subject={ntfAdminSubject}
            message={ntfAdminMessage}
            intervalNum={ntfAdminIntervalNum}
            intervalType={ntfAdminIntervalType}
            occurenceNum={ntfAdminOccurenceNum}
            recipientGroupId={ntfRecipientGroupId}
            recipientGroups={recipientGroups}
            recipientGroupEnabled={ntfRecipientGroupEnabled}
            setStatus={handleNtfEnableNotifyAdminChange}
            setToAccount={handleNtfToAccountChange}
            setNotifySettingType={handleNtfAdminNotifySettingTypeChange}
            setSubject={handleNtfAdminSubjectChange}
            setMessage={handleNtfAdminMessageChange}
            setIntervalNum={handleNtfAdminIntervalNumChange}
            setIntervalType={handleNtfAdminIntervalTypeChange}
            setOccurenceNum={handleNtfAdminOccurenceNumChange}
            setRecipientGroupId={handleNtfRecipientGroupIdChange}
            setRecipientGroupEnabled={handleNtfRecipientGroupEnabledChange}
          />
        </TabPanel>
        <TabPanel>
          <NotifyUser
            status={ntfEnableNotifyRecipient}
            subject={ntfInternalRecipientSubject}
            message={ntfInternalRecipientMessage}
            setStatus={handleNtfEnableNotifyRecipientChange}
            setSubject={handleNtfInternalRecipientSubjectChange}
            setMessage={handleNtfInternalRecipientMessageChange}
          />
        </TabPanel>
      </TabPanels>
    </Tabs>
  );
};
function ActionNotification({
  advancedOptionShow = false,
  availableTokenShow = false,
  replaceFileNameShow = false,
}: {
  advancedOptionShow?: boolean;
  availableTokenShow?: boolean;
  replaceFileNameShow?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <Box ml="1px" mt="16px" width="100%">
      <Flex direction="column">
        <TitleLabel title={t('policy.action')} mb="6x" />
        <VaAction />
        {advancedOptionShow ? (
          <Flex mb="6x" mt="4x">
            <VaAdvancedOption
              availableTokenShow={availableTokenShow}
              replaceFileNameShow={replaceFileNameShow}
            />
          </Flex>
        ) : null}
        <TitleLabel title={t('policy.notification')} mt="6x" />
        <VaNotification />
      </Flex>
    </Box>
  );
}

export default React.memo(ActionNotification);
